import React, { useState, useEffect } from "react";
import { useReveal } from "./hooks/ useReveal";
import Nav from "./components/Nav";
import Hero from "./components/Hero";
import Marquee from "./components/Marquee";
import About from "./components/About";
import Services from "./components/Services";
import Gallery from "./components/Gallery";
import Pricing from "./components/Pricing";
import Testimonials from "./components/Testimonials";
import Booking from "./components/Booking";
import Footer from "./components/Footer";
import "./styles/App.css";

function App() {
  useReveal();
  const [loading, setLoading] = useState(true);

  // Preload images
  useEffect(() => {
    const imagesToPreload = [
      "https://images.unsplash.com/photo-1544161515-4ab6ce6db874",
      "https://images.unsplash.com/photo-1552693673-1bf958298935",
      "https://images.unsplash.com/photo-1506126613408-eca07ce68773",
      "https://images.unsplash.com/photo-1519823551278-64ac92734fb1",
      "https://images.unsplash.com/photo-1600334129128-685c5582fd35",
      "https://images.unsplash.com/photo-1540555700478-4be289fbecef",
      "https://images.unsplash.com/photo-1515377905703-c4788e51af15",
      "https://images.unsplash.com/photo-1596462502278-27bfdc403348",
      "https://images.unsplash.com/photo-1501117716987-c8e1ecb2106d",
    ];

    let loadedCount = 0;
    const totalImages = imagesToPreload.length;

    imagesToPreload.forEach((url) => {
      const img = new Image();
      img.src = url;
      img.onload = () => {
        loadedCount++;
        if (loadedCount === totalImages) {
          setTimeout(() => setLoading(false), 500);
        }
      };
      img.onerror = () => {
        loadedCount++;
        if (loadedCount === totalImages) {
          setTimeout(() => setLoading(false), 500);
        }
      };
    });

    // Fallback timeout
    const timer = setTimeout(() => {
      setLoading(false);
    }, 3000);

    return () => clearTimeout(timer);
  }, []);

  // Custom cursor effect
  useEffect(() => {
    const cursor = document.getElementById("cursor");
    const cursorRing = document.getElementById("cursor-ring");

    if (!cursor || !cursorRing) return;

    let mx = 0,
      my = 0,
      rx = 0,
      ry = 0;

    const move = (e) => {
      mx = e.clientX;
      my = e.clientY;
      if (cursor) {
        cursor.style.left = mx + "px";
        cursor.style.top = my + "px";
      }
    };

    const animate = () => {
      rx += (mx - rx) * 0.12;
      ry += (my - ry) * 0.12;
      if (cursorRing) {
        cursorRing.style.left = rx + "px";
        cursorRing.style.top = ry + "px";
      }
      requestAnimationFrame(animate);
    };

    document.addEventListener("mousemove", move);
    animate();

    const interactiveElements = document.querySelectorAll(
      "a, button, .service-card, .price-card, .gallery-item, .testimonial-card"
    );
    interactiveElements.forEach((el) => {
      el.addEventListener("mouseenter", () => {
        cursor?.classList.add("hover");
        cursorRing?.classList.add("hover");
      });
      el.addEventListener("mouseleave", () => {
        cursor?.classList.remove("hover");
        cursorRing?.classList.remove("hover");
      });
    });

    return () => {
      document.removeEventListener("mousemove", move);
    };
  }, []);

  const openWhatsApp = () => {
    window.open(
      "https://wa.me/919521713060?text=Hello%21%20I%27m%20interested%20in%20your%20services",
      "_blank"
    );
  };

  return (
    <>
      {loading && (
        <div id="loader">
          <div className="loader-logo">
            Mani <span>Spa & Retreat</span>
          </div>
          <div className="loader-bar"></div>
          <div className="loader-text">Preparing your sanctuary</div>
        </div>
      )}
      <div id="cursor"></div>
      <div id="cursor-ring"></div>

      {/* WhatsApp Floating Button */}
      <button
        onClick={openWhatsApp}
        className="whatsapp-float"
        aria-label="Chat on WhatsApp"
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">
          <path
            fill="currentColor"
            d="M380.9 97.1C339 55.1 283.2 32 223.9 32c-122.4 0-222 99.6-222 222 0 39.1 10.2 77.3 29.6 111L0 480l117.7-30.9c32.4 17.7 68.9 27 106.1 27h.1c122.3 0 224.1-99.6 224.1-222 0-59.3-25.2-115-67.1-157zm-157 341.6c-33.2 0-65.7-8.9-94-25.7l-6.7-4-69.8 18.3L72 359.2l-4.4-7c-18.5-29.4-28.2-63.3-28.2-98.2 0-101.7 82.8-184.5 184.6-184.5 49.3 0 95.6 19.2 130.4 54.1 34.8 34.9 56.2 81.2 56.1 130.5 0 101.8-84.9 184.6-186.6 184.6zm101.2-138.2c-5.5-2.8-32.8-16.2-37.9-18-5.1-1.9-8.8-2.8-12.5 2.8-3.7 5.6-14.3 18-17.6 21.8-3.2 3.7-6.5 4.2-12 1.4-32.6-16.3-54-29.1-75.5-66-5.7-9.8 5.7-9.1 16.3-30.3 1.8-3.7.9-6.9-.5-9.7-1.4-2.8-12.5-30.2-17.2-41.3-4.5-10.8-9.1-9.3-12.5-9.5-3.2-.2-6.9-.2-10.6-.2-3.7 0-9.7 1.4-14.8 6.9-5.1 5.6-19.4 19-19.4 46.3 0 27.3 19.9 53.7 22.6 57.4 2.8 3.7 39.1 59.7 94.8 83.8 35.2 15.2 49 16.5 66.6 13.9 10.7-1.6 32.8-13.4 37.4-26.4 4.6-13 4.6-24.1 3.2-26.4-1.3-2.5-5-3.9-10.5-6.6z"
          />
        </svg>
      </button>

      <Nav />
      <Hero />
      <Marquee />
      <About />
      <Services />
      <Gallery />
      <Pricing />
      <Testimonials />
      <Booking />
      <Footer />
    </>
  );
}

export default App;
